import { showGeoPermissionGuide } from '@/components/common/GlobalLocationPermissionGuide';
import { classifyGeoError } from '@/lib/geoPermission';
/**
 * AcceptanceDialog - Coleta do aceite de um participante (Core de Aceites)
 *
 * Exibe o termo congelado na versão da política, coleta nome, CPF (quando exigido),
 * assinatura desenhada, selfie e localização, e envia à Edge Function `acceptance-confirm`.
 * As evidências não provam identidade; o texto informa isso ao participante.
 *
 * @version 1.3.0
 * RELEASE: 2026-10-13 v1.3.0 - Aceite do cliente com escolha Titular/Testemunha (prop
 *          `allowWitness`, congelada na versão do modelo): titular já traz o nome do
 *          proprietário e confirma CPF + nascimento; testemunha preenche os 3 campos.
 *          O marcador signer_kind é enviado e validado no servidor.
 * RELEASE: 2026-10-01 v1.2.0 - CPF é de quem está assinando (não precisa ser o proprietário) e
 *          data de nascimento quando o formato exige (requirements.birth_date).
 * RELEASE: 2026-09-27 v1.1.0 - Prop opcional `onConfirm` para reaproveitar o diálogo no link
 *          externo (/v/:token); sem ela, o envio segue pela Edge Function acceptance-confirm.
 * RELEASE: 2026-09-27 v1.0.0 - Versão inicial (Aceites Fase 3).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Camera, Loader2, MapPin, PenLine, RotateCcw, User, UserCheck, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { CameraCaptureDialog } from '@/components/checklist/inspection/CameraCaptureDialog';
import { SignaturePad, type SignaturePadHandle } from './SignaturePad';
import {
  ROLE_LABEL, useConfirmAcceptance, type AcceptanceParticipant, type AcceptanceRequirements,
  type ConfirmAcceptanceInput,
} from '@/hooks/useChecklistAcceptance';
// v1.3.0: regras puras do fluxo Titular/Testemunha (testadas em tests/regras)
import { acceptanceNameLocked, acceptanceSubmissionName, effectiveAcceptanceRequirements, signerKindForSubmission, witnessFlowEnabled } from '@/lib/acceptanceSignerKind';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  requestId: string;
  participant: AcceptanceParticipant;
  requirements: AcceptanceRequirements;
  terms: { title: string; body: string; declaration: string; version_number: number };
  defaultName?: string;
  /** v1.3.0: permite aceite do cliente por testemunha (vem congelado da versão do modelo) */
  allowWitness?: boolean;
  /** v1.1.0: envio alternativo (ex.: link externo). Deve lançar erro em caso de falha. */
  onConfirm?: (input: ConfirmAcceptanceInput) => Promise<void>;
}

/** Remove qualquer marcação e limita o tamanho do nome */
const sanitizeName = (v: string) => v.replace(/[<>]/g, '').slice(0, 150);
const maskCpf = (v: string) => v.replace(/\D/g, '').slice(0, 11)
  .replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');

const blobToDataUrl = (b: Blob) => new Promise<string>((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(String(r.result));
  r.onerror = rej;
  r.readAsDataURL(b);
});

export function AcceptanceDialog({ open, onOpenChange, requestId, participant, requirements, terms, defaultName, allowWitness, onConfirm }: Props) {
  const internalConfirm = useConfirmAcceptance();
  // v1.1.0: estado de envio quando o envio é injetado por quem usa o diálogo
  const [externalPending, setExternalPending] = useState(false);
  const confirm = { isPending: onConfirm ? externalPending : internalConfirm.isPending };
  const padRef = useRef<SignaturePadHandle>(null);
  // v1.3.0: escolha Titular/Testemunha somente no aceite do cliente com a opção habilitada
  const witnessFlow = witnessFlowEnabled(participant.role, allowWitness);
  const [signerKind, setSignerKind] = useState<'owner' | 'witness'>('owner');
  const [name, setName] = useState(defaultName ?? '');
  const [cpf, setCpf] = useState('');
  // v1.2.0: data de nascimento (AAAA-MM-DD, input nativo de data)
  const [birth, setBirth] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [hasDrawing, setHasDrawing] = useState(false);
  const [selfie, setSelfie] = useState<{ url: string; file: File } | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [geo, setGeo] = useState<ConfirmAcceptanceInput['geo']>(null);
  const [geoState, setGeoState] = useState<'idle' | 'loading' | 'granted' | 'denied' | 'unavailable'>('idle');
  // Chave estável por abertura: repetir o envio não duplica o aceite
  const idemKey = useMemo(() => `acc_${crypto.randomUUID().replace(/-/g, '')}`, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    setSignerKind('owner');
    setName(defaultName ?? ''); setCpf(''); setBirth(''); setAgreed(false); setHasDrawing(false);
    setSelfie(null); setGeo(null); setGeoState('idle');
  }, [open, defaultName]);

  // v1.3.0: ao trocar para testemunha, o nome do titular não é reaproveitado
  const changeSignerKind = (kind: 'owner' | 'witness') => {
    setSignerKind(kind);
    setName(kind === 'owner' ? (defaultName ?? '') : '');
  };

  const captureLocation = () => {
    if (!navigator.geolocation) { setGeoState('unavailable'); return; }
    setGeoState('loading');
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setGeo({ latitude: p.coords.latitude, longitude: p.coords.longitude, accuracy: p.coords.accuracy,
          captured_at_client: new Date(p.timestamp).toISOString(), permission_status: 'granted' });
        setGeoState('granted');
      },
      (err) => { setGeoState(err.code === err.PERMISSION_DENIED ? 'denied' : 'unavailable'); showGeoPermissionGuide(classifyGeoError(err.code)); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  const cpfDigits = cpf.replace(/\D/g, '');
  // O titular apenas consulta o nome congelado; digitação continua liberada para testemunha/equipe.
  const nameLocked = acceptanceNameLocked(participant.role, allowWitness, signerKind);
  const submissionName = acceptanceSubmissionName(participant.role, allowWitness, signerKind, defaultName, name);
  // v1.3.0: com testemunha habilitada, CPF e nascimento passam a ser sempre exigidos
  // (titular confirma os próprios dados; testemunha informa os dela — o servidor revalida)
  const effReq = effectiveAcceptanceRequirements(requirements, participant.role, allowWitness);
  const missing: string[] = [];
  if (submissionName.length < 2) missing.push(nameLocked ? 'nome do cliente no cadastro — solicite a correção ao gestor' : 'nome');
  if (effReq.document_cpf && cpfDigits.length !== 11) missing.push('CPF');
  if (effReq.birth_date && !/^\d{4}-\d{2}-\d{2}$/.test(birth)) missing.push('data de nascimento');
  if (effReq.signature_drawing && !hasDrawing) missing.push('assinatura');
  if (effReq.selfie && !selfie) missing.push('selfie');
  if (effReq.geolocation && !geo) missing.push('localização');
  if (!agreed) missing.push('confirmação da declaração');

  const handleConfirm = async () => {
    if (missing.length) return toast.error(`Falta: ${missing.join(', ')}`);
    const artifacts: ConfirmAcceptanceInput['artifacts'] = [];
    const drawing = padRef.current?.toDataUrl();
    if (drawing) artifacts.push({ kind: 'signature_drawing', data_base64: drawing });
    if (selfie) artifacts.push({ kind: 'selfie', data_base64: await blobToDataUrl(selfie.file) });
    const input: ConfirmAcceptanceInput = {
      request_id: requestId,
      participant_id: participant.id,
      idempotency_key: idemKey,
      signer_name: submissionName,
      signer_document: cpfDigits || null,
      signer_birth_date: birth || null,
      // v1.3.0: quem está assinando (titular/testemunha) — somente no aceite do cliente
      signer_kind: signerKindForSubmission(participant.role, allowWitness, signerKind),
      geo,
      geo_result: geo ? 'granted' : geoState === 'denied' ? 'denied' : geoState === 'unavailable' ? 'unavailable' : 'not_requested',
      device: {
        platform: navigator.platform?.slice(0, 80) ?? '',
        language: navigator.language?.slice(0, 20) ?? '',
        screen: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone?.slice(0, 60) ?? '',
      },
      artifacts,
    };
    if (onConfirm) {
      setExternalPending(true);
      try { await onConfirm(input); onOpenChange(false); }
      catch (e) { toast.error((e as Error).message || 'Não foi possível registrar o aceite'); }
      finally { setExternalPending(false); }
      return;
    }
    internalConfirm.mutate(input, { onSuccess: () => onOpenChange(false) });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !confirm.isPending && onOpenChange(v)}>
      {/* Assinar não é um gesto de fechar: desativa o arrasto apenas neste diálogo. */}
      <DialogContent enableSwipeToClose={false} className="flex max-h-[92dvh] w-[calc(100vw-1rem)] max-w-2xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b px-6 py-4 text-left">
          <DialogTitle className="flex items-center gap-2 text-base"><PenLine className="h-4 w-4 text-primary" />{terms.title}</DialogTitle>
          <DialogDescription className="text-xs">
            Aceite de {ROLE_LABEL[participant.role]} · termo v{terms.version_number}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-6 py-5">
          <div className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded-md border bg-muted/40 p-3 text-xs leading-relaxed">{terms.body}</div>

          {/* v1.3.0: escolha de quem está assinando (somente aceite do cliente com testemunha habilitada) */}
          {witnessFlow && (
            <div className="space-y-2">
              <Label>Quem está assinando?</Label>
              <div className="grid grid-cols-2 gap-2">
                {([
                  { kind: 'owner' as const, label: 'Titular (Cliente)', hint: 'O próprio proprietário confirma os dados', icon: UserCheck },
                  { kind: 'witness' as const, label: 'Testemunha', hint: 'Outra pessoa assina no local', icon: User },
                ]).map(({ kind, label, hint, icon: Icon }) => (
                  <button key={kind} type="button" onClick={() => changeSignerKind(kind)}
                    aria-pressed={signerKind === kind}
                    className={`rounded-xl border p-3 text-left transition-colors ${signerKind === kind
                      ? 'border-primary bg-primary/5 ring-1 ring-primary/40'
                      : 'border-border hover:border-primary/40'}`}>
                    <span className="flex items-center gap-1.5 text-sm font-medium"><Icon className="h-4 w-4 text-primary" />{label}</span>
                    <span className="mt-0.5 block text-[11px] text-muted-foreground">{hint}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Nome completo{witnessFlow && signerKind === 'witness' ? ' da testemunha' : ''}</Label>
              <Input aria-label="Nome completo" value={nameLocked ? defaultName ?? '' : name} disabled={nameLocked} maxLength={150} onChange={(e) => setName(sanitizeName(e.target.value))} />
              {nameLocked && !submissionName && <p className="text-xs text-destructive">Nome do cliente não informado. Solicite a correção ao gestor.</p>}</div>
            {effReq.document_cpf && (
              <div className="space-y-1.5"><Label>CPF de quem está assinando</Label>
                <Input value={cpf} inputMode="numeric" onChange={(e) => setCpf(maskCpf(e.target.value))} placeholder="000.000.000-00" /></div>
            )}
            {/* v1.2.0: data de nascimento do signatário, quando o formato exige */}
            {effReq.birth_date && (
              <div className="space-y-1.5"><Label>Data de nascimento</Label>
                <Input type="date" value={birth} max={new Date().toISOString().slice(0, 10)} min="1900-01-01"
                  onChange={(e) => setBirth(e.target.value.slice(0, 10))} /></div>
            )}
          </div>

          {effReq.signature_drawing && (
            <div className="space-y-1.5"><Label>Assinatura</Label>
              <SignaturePad ref={padRef} onChange={setHasDrawing} disabled={confirm.isPending} /></div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            {requirements.selfie && (
              <div className="space-y-1.5">
                <Label>Selfie</Label>
                {selfie ? (
                  <div className="relative w-fit">
                    <img src={selfie.url} alt="Selfie capturada" className="h-28 w-28 rounded-md border object-cover" />
                    <Button type="button" size="icon" variant="secondary" className="absolute -right-2 -top-2 h-6 w-6" onClick={() => setCameraOpen(true)} title="Refazer">
                      <RotateCcw className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <Button type="button" variant="outline" size="sm" onClick={() => setCameraOpen(true)}><Camera className="mr-1.5 h-4 w-4" />Tirar selfie</Button>
                )}
              </div>
            )}
            {requirements.geolocation && (
              <div className="space-y-1.5">
                <Label>Localização</Label>
                <Button type="button" variant="outline" size="sm" onClick={captureLocation} disabled={geoState === 'loading'}>
                  {geoState === 'loading' ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <MapPin className="mr-1.5 h-4 w-4" />}
                  {geo ? 'Localização registrada' : 'Registrar localização'}
                </Button>
                {geo && <p className="text-[11px] text-muted-foreground">Precisão aproximada: {Math.round(geo.accuracy ?? 0)} m</p>}
                {geoState === 'denied' && <p className="text-[11px] text-destructive">Permita o acesso à localização no navegador.</p>}
                {geoState === 'unavailable' && <p className="text-[11px] text-destructive">Localização indisponível neste aparelho.</p>}
              </div>
            )}
          </div>

          <label className="flex items-start gap-2 rounded-md border p-3 text-xs">
            <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} className="mt-0.5" />
            <span>{terms.declaration}</span>
          </label>
          <p className="text-[11px] text-muted-foreground">
            Serão registrados data e hora do servidor, endereço IP, navegador e as evidências acima. A localização é a informada pelo navegador e a selfie é usada apenas como registro, sem reconhecimento facial.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 border-t bg-card px-6 py-3">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={confirm.isPending}><X className="mr-1 h-3.5 w-3.5" />Cancelar</Button>
          <Button size="sm" className="bg-success text-success-foreground hover:bg-success/90" onClick={handleConfirm} disabled={confirm.isPending}>
            {confirm.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}Confirmar aceite
          </Button>
        </div>
      </DialogContent>
      <CameraCaptureDialog open={cameraOpen} onOpenChange={setCameraOpen} facing="user"
        onCapture={(file) => { setSelfie({ url: URL.createObjectURL(file), file }); return true; }} />
    </Dialog>
  );
}
